import { Unit, enumUnit } from '@rematter/frontend-lib-api-client';

import type {
  ProcessingTypeInputItemRecord,
  ProcessingTypeOutputItemRecord,
} from '../types/processingType';

export type CalculationDirection = 'FromInputs' | 'FromOutputs';

export type CalculatedRecipeInput = {
  itemId: string;
  quantityUnit: Unit;
  quantity: number;
  isPrimary: boolean;
};

export type CalculatedRecipeOutput = {
  itemId: string;
  quantityUnit: Unit;
  quantity: number;
  isPrimary: boolean;
  isNoCost: boolean;
  costAllocationPercentage?: number | null;
};

export type CalculatedRecipeResult = {
  inputs: CalculatedRecipeInput[];
  outputs: CalculatedRecipeOutput[];
};

const massConversionsToPounds: Record<Unit, number> = {
  [enumUnit.Pounds]: 1,
  [enumUnit.Ounces]: 0.0625,
  [enumUnit.Kilograms]: 2.20462,
  [enumUnit.Grams]: 0.00220462,
  [enumUnit.MetricTons]: 2204.62,
  [enumUnit.ShortTons]: 2000,
  [enumUnit.LongTons]: 2240,
  [enumUnit.NetTons]: 2000,
  [enumUnit.GrossTons]: 2240,
  [enumUnit.Each]: 1,
  [enumUnit.ShortHundredweight]: 100,
  [enumUnit.LongHundredweight]: 112,
  [enumUnit.Yard]: 0,
  [enumUnit.Meter]: 0,
};

export function convertQuantity(
  qty: number,
  fromUnit: Unit,
  toUnit: Unit,
): number {
  if (fromUnit === toUnit) {
    return qty;
  }

  const fromFactor = massConversionsToPounds[fromUnit] ?? 1;
  const toFactor = massConversionsToPounds[toUnit] ?? 1;

  const poundsQty = qty * fromFactor;
  return poundsQty / toFactor;
}

export function calculateFromInput(
  inputItems: ProcessingTypeInputItemRecord[],
  outputItems: ProcessingTypeOutputItemRecord[],
  inputQuantity: number,
  inputUnit: Unit,
): CalculatedRecipeResult | null {
  const primaryInput = inputItems.find((i) => i.isPrimary);
  if (!primaryInput) {
    return null;
  }

  const result: CalculatedRecipeResult = {
    inputs: [
      {
        itemId: primaryInput.itemId,
        quantityUnit: inputUnit,
        quantity: inputQuantity,
        isPrimary: true,
      },
    ],
    outputs: [],
  };

  for (const output of outputItems) {
    let outputQty = inputQuantity * (output.yieldPercentage / 100);

    if (output.outputUnit !== inputUnit) {
      outputQty = convertQuantity(outputQty, inputUnit, output.outputUnit);
    }

    result.outputs.push({
      itemId: output.itemId,
      quantityUnit: output.outputUnit,
      quantity: outputQty,
      isPrimary: output.isPrimary,
      isNoCost: output.isNoCost,
      costAllocationPercentage: output.costAllocationPercentage,
    });
  }

  return result;
}

export function calculateFromOutput(
  inputItems: ProcessingTypeInputItemRecord[],
  outputItems: ProcessingTypeOutputItemRecord[],
  outputItemId: string,
  desiredQuantity: number,
  desiredUnit: Unit,
): CalculatedRecipeResult | null {
  const refOutput = outputItems.find((o) => o.itemId === outputItemId);
  if (!refOutput) {
    return null;
  }

  if (refOutput.yieldPercentage <= 0) {
    return null;
  }

  let workingQty = desiredQuantity;
  if (refOutput.outputUnit !== desiredUnit) {
    workingQty = convertQuantity(
      desiredQuantity,
      desiredUnit,
      refOutput.outputUnit,
    );
  }

  const inputQty = (workingQty * 100) / refOutput.yieldPercentage;

  return calculateFromInput(
    inputItems,
    outputItems,
    inputQty,
    refOutput.outputUnit,
  );
}

export function hasRecipe(
  inputItems?: ProcessingTypeInputItemRecord[],
  outputItems?: ProcessingTypeOutputItemRecord[],
): boolean {
  return (
    (inputItems !== undefined && inputItems.length > 0) ||
    (outputItems !== undefined && outputItems.length > 0)
  );
}
